import { useMutation, useQuery, useQueryClient } from 'react-query';

import { PROVIDER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { IStaff } from '~/Models/DTO/Staff.model';
import { routeReplacer } from '~/Helpers/String.helper';
import { useProvider } from '~/Hooks';

export const useRequestStaff = (staffId: string) => {
  const { currentProvider } = useProvider();
  return useQuery<IStaff, IErrorResponse>(
    [REQUEST_ACTIONS.STAFF_UPDATE, staffId],
    async () =>
      requestReactQuery<IStaff>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.STAFF_DETAIL, {
          staffId,
          providerId: currentProvider?.uuid || '',
        }),
        config: { method: 'GET' },
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: !!staffId && !!currentProvider?.uuid,
    },
  );
};

export const useRequestStaffUpdate = () => {
  const { currentProvider } = useProvider();
  const queryClient = useQueryClient();
  return useMutation<IStaff, IErrorResponse, IStaff>(async (staff) => {
    const response = await requestReactQuery<IStaff>({
      url: routeReplacer(PROVIDER_SERVICE_URLS.STAFF_UPDATE, { staffId: staff.id }),
      data: { ...staff, providerId: currentProvider?.uuid },
      config: { method: 'PATCH' },
    });
    queryClient.invalidateQueries(REQUEST_ACTIONS.STAFF_LIST);
    queryClient.invalidateQueries([REQUEST_ACTIONS.STAFF_UPDATE, staff.id]);
    return response;
  });
};

export const useRequestStaffCreate = () => {
  const { currentProvider } = useProvider();
  const queryClient = useQueryClient();
  return useMutation<IStaff, IErrorResponse, IStaff>(async (staff) => {
    const response = await requestReactQuery<IStaff>({
      url: PROVIDER_SERVICE_URLS.STAFF_LIST,
      data: { ...staff, providerId: currentProvider?.uuid },
      config: { method: 'POST' },
    });
    queryClient.invalidateQueries(REQUEST_ACTIONS.STAFF_LIST);
    return response;
  });
};

export const useRequestDeactivateStaff = () => {
  const { currentProvider } = useProvider();
  const queryClient = useQueryClient();

  return useMutation<
    IStaff,
    IErrorResponse,
    { staffId: string; substituteId?: string; disabled?: boolean }
  >(async ({ staffId, substituteId, disabled = true }) => {
    const response = await requestReactQuery<IStaff>({
      url: routeReplacer(PROVIDER_SERVICE_URLS.STAFF_UPDATE, { staffId }),
      data: { disabled, substituteId, providerId: currentProvider?.uuid },
      config: { method: 'PATCH' },
    });
    queryClient.invalidateQueries(REQUEST_ACTIONS.STAFF_LIST);
    queryClient.invalidateQueries([REQUEST_ACTIONS.STAFF_UPDATE, staffId]);
    return response;
  });
};

export const useRequestDeleteStaff = () => {
  const { currentProvider } = useProvider();
  const queryClient = useQueryClient();

  return useMutation<IStaff, IErrorResponse, { staffId: string }>(async ({ staffId }) => {
    const response = await requestReactQuery<IStaff>({
      url: routeReplacer(PROVIDER_SERVICE_URLS.STAFF_UPDATE, { staffId }),
      data: { providerId: currentProvider?.uuid },
      config: { method: 'DELETE' },
    });
    queryClient.invalidateQueries(REQUEST_ACTIONS.STAFF_LIST);
    queryClient.invalidateQueries([REQUEST_ACTIONS.STAFF_UPDATE, staffId]);
    return response;
  });
};
